import { useState, useCallback } from 'react';

export interface AccessProvisioning {
  vpn: boolean;
  hrSystem: boolean;
  hubSpot: boolean;
  fileServer: boolean;
  sage: boolean;
}

export interface HardwareConfig {
  isReused: boolean;
  serviceTag?: string;
  type?: 'Laptop' | 'Desktop';
  primaryDisplay: boolean;
  secondaryDisplay: boolean;
  mouse: boolean;
  keyboard: boolean;
  laptopBag: boolean;
}

export interface OnboardingFormData {
  // User Details
  firstName: string;
  lastName: string;
  displayName: string;
  jobTitle: string;
  department: string;
  role: 'Employee' | 'Contractor';
  mobilePhone: string;
  businessPhone: string;
  siteCode: string;
  lineManagerId: string;
  lineManagerName: string;
  lineManagerEmail: string;
  startDate: Date | null;
  endDate: Date | null;

  // Access & Provisioning
  accessProvisioning: AccessProvisioning;

  // Software
  additionalSoftware: string[];

  // Hardware
  hardware: HardwareConfig;

  // Additional
  additionalRequests: string;
}

const initialFormData: OnboardingFormData = {
  firstName: '',
  lastName: '',
  displayName: '',
  jobTitle: '',
  department: '',
  role: 'Employee',
  mobilePhone: '',
  businessPhone: '',
  siteCode: '',
  lineManagerId: '',
  lineManagerName: '',
  lineManagerEmail: '',
  startDate: new Date(),
  endDate: null,
  accessProvisioning: {
    vpn: false,
    hrSystem: false,
    hubSpot: false,
    fileServer: true,
    sage: false,
  },
  additionalSoftware: [],
  hardware: {
    isReused: false,
    primaryDisplay: false,
    secondaryDisplay: false,
    mouse: false,
    keyboard: false,
    laptopBag: false,
  },
  additionalRequests: '',
};

export function useOnboardingForm() {
  const [currentStep, setCurrentStep] = useState(0);
  const [formData, setFormData] = useState<OnboardingFormData>(initialFormData);
  const [validationErrors, setValidationErrors] = useState<Record<string, string>>({});

  const updateFormData = useCallback(
    <K extends keyof OnboardingFormData>(
      field: K,
      value: OnboardingFormData[K]
    ) => {
      setFormData((prev) => {
        const updated = { ...prev, [field]: value };

        // Auto-generate display name
        if (field === 'firstName' || field === 'lastName') {
          const firstName = field === 'firstName' ? (value as string) : prev.firstName;
          const lastName = field === 'lastName' ? (value as string) : prev.lastName;
          if (firstName || lastName) {
            updated.displayName = `${firstName} ${lastName}`.trim();
          }
        }

        return updated;
      });

      // Clear validation error for this field
      if (validationErrors[field]) {
        setValidationErrors((prev) => {
          const newErrors = { ...prev };
          delete newErrors[field];
          return newErrors;
        });
      }
    },
    [validationErrors]
  );

  const validateStep = useCallback((stepIndex: number): boolean => {
    const errors: Record<string, string> = {};

    switch (stepIndex) {
      case 0: // User Details
        if (!formData.firstName.trim()) errors.firstName = 'First name is required';
        if (!formData.lastName.trim()) errors.lastName = 'Last name is required';
        if (!formData.jobTitle.trim()) errors.jobTitle = 'Job title is required';
        if (!formData.department.trim()) errors.department = 'Department is required';
        if (!formData.siteCode) errors.siteCode = 'Site code is required';
        if (!formData.lineManagerId) errors.lineManager = 'Line manager is required';
        if (!formData.startDate) errors.startDate = 'Start date is required';
        break;

      case 3: // Hardware
        if (formData.hardware.isReused && !formData.hardware.serviceTag) {
          errors.serviceTag = 'Service tag is required for reused PC';
        }
        if (!formData.hardware.isReused && !formData.hardware.type) {
          errors.hardwareType = 'Please select laptop or desktop';
        }
        break;
    }

    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  }, [formData]);

  const nextStep = useCallback(() => {
    if (currentStep < 5) {
      setCurrentStep((prev) => prev + 1);
    }
  }, [currentStep]);

  const prevStep = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep((prev) => prev - 1);
    }
  }, [currentStep]);

  const goToStep = useCallback((stepIndex: number) => {
    if (stepIndex >= 0 && stepIndex <= currentStep) {
      setCurrentStep(stepIndex);
    }
  }, [currentStep]);

  const resetForm = useCallback(() => {
    setFormData(initialFormData);
    setCurrentStep(0);
    setValidationErrors({});
  }, []);

  return {
    currentStep,
    formData,
    updateFormData,
    validationErrors,
    validateStep,
    nextStep,
    prevStep,
    goToStep,
    resetForm,
  };
}
